import React, { useState } from "react";
import { CheckCircle2, XCircle, Medal, RotateCcw, Zap, ChevronDown, ChevronRight } from "lucide-react";
import { FIELD_TITLE } from "@/lib/triage";

const label = (f) => (f === "decision" ? "Decision" : FIELD_TITLE[f] || f);
const show = (v) => (Array.isArray(v) ? v.join(", ") || "–" : v || "–");

function TicketResult({ g, t }) {
  const [open, setOpen] = useState(g.points < g.max);
  return (
    <div className="rounded-xl border border-zinc-800 bg-[#111113]" data-testid={`sim-result-${g.id}`}>
      <button onClick={() => setOpen(!open)} className="flex w-full items-center gap-3 p-4 text-left">
        {open ? <ChevronDown size={15} className="text-zinc-500" /> : <ChevronRight size={15} className="text-zinc-500" />}
        <span className="font-mono text-[13px] text-zinc-200">{g.reference}</span>
        <span className="truncate text-[13px] text-zinc-500">{t?.platform} · {t?.reporter_name}</span>
        <span className={`ml-auto rounded-full px-2.5 py-0.5 text-[12px] font-semibold ${g.points === g.max ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>{g.points}/{g.max}</span>
      </button>
      {open && (
        <div className="border-t border-zinc-800 p-4">
          <div className="space-y-2">
            {g.rows.map((r) => (
              <div key={r.field} className="grid gap-1 text-[13px] sm:grid-cols-[180px_1fr_60px]">
                <span className="text-zinc-400">{label(r.field)}</span>
                <span className="text-zinc-200">
                  {r.ok ? <CheckCircle2 size={14} className="mr-1 inline text-emerald-400" /> : <XCircle size={14} className="mr-1 inline text-red-400" />}
                  {show(r.yours)}
                  {!r.ok && <span className="block text-[12px] text-emerald-300/90">Expected: {show(r.accepted)}</span>}
                </span>
                <span className="text-right text-zinc-500">{r.points}/{r.max}</span>
              </div>
            ))}
          </div>
          <p className="mt-4 rounded-lg border border-blue-500/25 bg-blue-500/[0.06] p-3 text-[13px] leading-relaxed text-zinc-200">{g.explanation}</p>
        </div>
      )}
    </div>
  );
}

export function OpsEvaluation({ res, tickets, onRetry, onBack }) {
  const byId = Object.fromEntries(tickets.map((t) => [t.id, t]));
  return (
    <div className="space-y-5" data-testid="sim-evaluation">
      <div className="rounded-2xl border border-zinc-800/70 bg-gradient-to-br from-[#0b1430] to-[#0c0c0e] p-6 text-center">
        <div className={`mx-auto grid h-20 w-20 place-items-center rounded-full ${res.passed ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}>{res.passed ? <Medal size={36} /> : <RotateCcw size={32} />}</div>
        <div className="mt-3 text-[12px] uppercase tracking-[0.2em] text-zinc-500">Shift evaluation</div>
        <div className="mt-1 text-5xl font-bold text-zinc-50" data-testid="sim-result-pct">{res.pct}%</div>
        <p className="mt-1 text-[14px] text-zinc-400">{res.points} of {res.max} points · pass mark {res.pass_pct}%</p>
        <p className="mt-3 text-[15px] text-zinc-200" data-testid="sim-result-verdict">{res.passed ? `Shift passed. You earned the “${res.badge}” badge.` : "Not yet. Review the feedback below and start a new shift."}</p>
        {res.xp_gained > 0 && <p className="mt-2 inline-flex items-center gap-1 text-[14px] font-semibold text-[#FFCC00]"><Zap size={15} /> +{res.xp_gained} XP</p>}
        <div className="mt-5 flex justify-center gap-3">
          <button data-testid="sim-retry" onClick={onRetry} className="rounded-full border border-zinc-700 px-4 py-2 text-[13px] text-zinc-200 hover:bg-zinc-800">Start a new shift</button>
          <button data-testid="sim-back-dashboard" onClick={onBack} className="rounded-full bg-blue-600 px-4 py-2 text-[13px] font-semibold text-white hover:bg-blue-500">Back to dashboard</button>
        </div>
      </div>
      {res.tickets.map((g) => <TicketResult key={g.id} g={g} t={byId[g.id]} />)}
    </div>
  );
}
